import React from "react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "src/components/ui/button";
import { Card, CardHeader, CardContent } from "src/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { Check, Bell, BellDot, Languages } from "lucide-react";
import { useNotification } from "../../context/NotificationContext";
import { Skeleton } from "src/components/ui/skeleton";
import { useLanguage } from "../../context/LanguageContext";

const NotificationsPage = () => {
  const { language } = useLanguage();

  const { notifications, loadingNotifications, markAsRead } = useNotification();

  const unreadNotifications = notifications.filter(
    (notification) => notification.is_read === 0
  );
  const readNotifications = notifications.filter(
    (notification) => notification.is_read === 1
  );

  const markAllAsRead = () => {};

  return (
    <div className="space-y-6" dir={language === "en" ? "ltr" : "rtl"}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Notifications</h1>
        {/* {unreadNotifications.length > 0 && (
          <Button
            variant="outline"
            onClick={markAllAsRead}
            className="flex items-center gap-2"
          >
            <Check className="h-4 w-4" />
            <span>Mark all as read</span>
          </Button>
        )} */}
      </div>

      <Tabs defaultValue="all" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="all" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <span>All ({notifications.length})</span>
          </TabsTrigger>
          <TabsTrigger value="unread" className="flex items-center gap-2">
            <BellDot className="h-4 w-4" />
            <span>Unread ({unreadNotifications.length})</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {loadingNotifications ? (
            <div className="space-y-6">
              <Skeleton className="w-full h-40" />
              <Skeleton className="w-full h-40" />
              <Skeleton className="w-full h-40" />
              <Skeleton className="w-full h-40" />
            </div>
          ) : notifications.length > 0 ? (
            <div className="space-y-4">
              {notifications.map((notification) => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                />
              ))}
            </div>
          ) : (
            <EmptyState message="No notifications" />
          )}
        </TabsContent>

        <TabsContent value="unread">
          {unreadNotifications.length > 0 ? (
            <div className="space-y-4">
              {unreadNotifications.map((notification) => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                />
              ))}
            </div>
          ) : (
            <EmptyState message="No unread notifications" />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const NotificationCard = ({ notification, onMarkAsRead }) => {
  const isUnread = notification.is_read === 0;

  return (
    <Card className={`${isUnread ? "border-l-4 border-l-blue-500" : ""}`}>
      <CardHeader className="flex flex-row items-start justify-between pb-2">
        <div>
          <h3 className="font-medium">{notification.title}</h3>
          <p className="text-sm text-muted-foreground">
            {formatDistanceToNow(new Date(notification.created_at), {
              addSuffix: true,
            })}
          </p>
        </div>
        {isUnread && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onMarkAsRead(notification.id)}
          >
            Mark as read
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <p>{notification.message}</p>
      </CardContent>
    </Card>
  );
};

const EmptyState = ({ message }) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Bell className="h-12 w-12 text-muted-foreground mb-4" />
      <h3 className="text-xl font-medium">{message}</h3>
      <p className="text-muted-foreground mt-2">
        When you receive notifications, they will appear here
      </p>
    </div>
  );
};

export default NotificationsPage;
